import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export type TemplateSection = {
  key: string;
  heading: string;
  description: string;
  required: boolean;
  fields?: { name: string; description?: string; required?: boolean }[];
};

export type ProposalSection = TemplateSection & { rowId: string };

let nextRowId = 0;
function newRowId() {
  nextRowId += 1;
  return `section-${nextRowId}`;
}

export function toProposalSections(sections: TemplateSection[]): ProposalSection[] {
  return sections.map((section) => ({ ...section, rowId: newRowId() }));
}

export function toTemplateSections(rows: ProposalSection[]): TemplateSection[] {
  const used = new Set<string>();
  return rows.flatMap(({ rowId: _rowId, ...section }) => {
    const heading = section.heading.trim();
    if (!heading) return [];
    const slug = heading.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    const base = section.key.trim() || slug || "section";
    let key = base;
    let suffix = 2;
    while (used.has(key)) {
      key = `${base}_${suffix}`;
      suffix += 1;
    }
    used.add(key);
    return [{ ...section, heading, description: section.description.trim(), key }];
  });
}

export function blankProposalSection(): ProposalSection {
  return {
    rowId: newRowId(),
    key: "",
    heading: "",
    description: "",
    required: true,
  };
}

function moveRow(rows: ProposalSection[], index: number, delta: -1 | 1): ProposalSection[] {
  const target = index + delta;
  if (target < 0 || target >= rows.length) return rows;
  const next = [...rows];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

type TemplateProposalEditorProps = {
  rows: ProposalSection[];
  onChange: (rows: ProposalSection[]) => void;
  disabled?: boolean;
};

export function TemplateProposalEditor({ rows, onChange, disabled = false }: TemplateProposalEditorProps) {
  const patchRow = (rowId: string, patch: Partial<ProposalSection>) => {
    onChange(rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Edit headings and descriptions, mark required sections, reorder, add, or delete. Nothing is saved until you save the template.
      </p>
      <ol className="w-full min-w-0 rounded-md border border-border text-sm" data-testid="template-proposal-rows" aria-label="Proposed template sections">
        {rows.map((row, index) => {
          const position = index + 1;
          return (
            <li key={row.rowId} className="border-b border-border last:border-b-0" data-testid="template-proposal-row">
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="w-7 shrink-0 pt-2 tabular-nums text-muted-foreground">{position}.</span>
                <div className="min-w-0 flex-1 space-y-2">
                  <Input
                    value={row.heading}
                    onChange={(event) => patchRow(row.rowId, { heading: event.target.value })}
                    disabled={disabled}
                    aria-label={`Heading for section ${position}`}
                    placeholder="Section heading"
                  />
                  <Textarea
                    value={row.description}
                    onChange={(event) => patchRow(row.rowId, { description: event.target.value })}
                    disabled={disabled}
                    aria-label={`Description for section ${position}`}
                    placeholder="What belongs in this section"
                    rows={2}
                  />
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={row.required}
                      disabled={disabled}
                      onChange={(event) => patchRow(row.rowId, { required: event.target.checked })}
                      aria-label={`Required for section ${position}`}
                    />
                    Required
                  </label>
                </div>
                <div className="flex shrink-0 flex-col gap-1">
                  <Button type="button" variant="ghost" size="icon" disabled={disabled || index === 0} aria-label={`Move section ${position} up`} onClick={() => onChange(moveRow(rows, index, -1))}>
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" disabled={disabled || index === rows.length - 1} aria-label={`Move section ${position} down`} onClick={() => onChange(moveRow(rows, index, 1))}>
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" disabled={disabled} aria-label={`Delete section ${position}`} onClick={() => onChange(rows.filter((candidate) => candidate.rowId !== row.rowId))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => onChange([...rows, blankProposalSection()])}>
        <Plus className="mr-1 h-4 w-4" />
        Add section
      </Button>
    </div>
  );
}
